import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import VoucherListModal from 'components/pdp/carousel-images/voucher/voucher-modal/VoucherListModal'
import { getVoucherCheckoutLists } from 'components/pdp/carousel-images/voucher/voucher-checkout/utils'
import VoucherApiHandler from 'lib/api/voucher'
import { customFlagValue } from 'lib/utils/handle-flag/const'

// Cart responses are JSON. Preserve every field that could affect eligibility,
// but ignore object identity and key order when deciding whether to fetch again.
const contextSignature = (context) =>
  JSON.stringify(context, (key, value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
      return value
    return Object.keys(value)
      .sort()
      .reduce((sorted, field) => ({ ...sorted, [field]: value[field] }), {})
  })

const VoucherCheckout = ({
  onClose,
  applyVoucher,
  cart,
  customerId,
  isGuestMode,
  shop,
  country,
  language,
}) => {
  const applied_voucher = cart?.summary?.applied_voucher
  const [isLoading, setIsLoading] = useState(true)
  const [allVouchersList, setAllVouchersList] = useState([])
  const [loadedContext, setLoadedContext] = useState(null)
  const confirmedVoucher =
    applied_voucher?.is_applied === true ? applied_voucher.code : ''
  // An untouched modal follows cart refreshes without submitting a voucher.
  const [selectedVoucher, setSelectedVoucher] = useState(null)
  const voucherCheckoutNewErrorFlagValue = customFlagValue({
    flagName: 'voucherCheckout',
  }) // TODO: force value to handle with flagsmith later (VoucherCheckoutNewError-20230101)

  const requestContext = contextSignature({
    cart,
    customerId,
    isGuestMode,
    shop,
    country,
    language,
    voucherCheckoutNewErrorFlagValue,
  })
  const isCurrentContextLoading = isLoading || loadedContext !== requestContext

  const handleClose = () => {
    if (
      !isCurrentContextLoading &&
      selectedVoucher !== null &&
      selectedVoucher !== confirmedVoucher
    ) {
      applyVoucher({
        type: 'USE_VOUCHER',
        voucherCode: selectedVoucher,
        apply_method: 'voucher_checkout',
      })
    }
    onClose()
  }

  useEffect(() => {
    let active = true
    const context = JSON.parse(requestContext)
    setIsLoading(true)
    setAllVouchersList([])
    setSelectedVoucher(null)

    if (context.cart?.summary && Array.isArray(context.cart.items)) {
      // Keep the normal uncached endpoint; there is no cross-cart/user cache.
      VoucherApiHandler.getVoucher(true)
        .then((res) => {
          if (!active) return
          getVoucherCheckoutLists(
            res.vouchers,
            context.cart.items,
            context.cart.summary.subtotal,
            setAllVouchersList,
            context.voucherCheckoutNewErrorFlagValue,
          )
          setLoadedContext(requestContext)
          setIsLoading(false)
        })
        .catch(() => {
          if (!active) return
          setAllVouchersList([])
          setLoadedContext(requestContext)
          setIsLoading(false)
        })
    }

    return () => {
      active = false
    }
  }, [requestContext])

  return (
    <VoucherListModal
      isLoading={isCurrentContextLoading}
      onClose={handleClose}
      voucherList={allVouchersList}
      onVoucherSelected={setSelectedVoucher}
      appliedVoucher={selectedVoucher ?? confirmedVoucher}
      isCheckout
    />
  )
}

VoucherCheckout.defaultProps = {
  customerId: null,
  isGuestMode: true,
  shop: null,
  country: '',
  language: '',
}

VoucherCheckout.propTypes = {
  onClose: PropTypes.func.isRequired,
  applyVoucher: PropTypes.func.isRequired,
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isGuestMode: PropTypes.bool,
  shop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  country: PropTypes.string,
  language: PropTypes.string,
  cart: PropTypes.shape({
    summary: PropTypes.shape({
      subtotal: PropTypes.number,
      applied_voucher: PropTypes.shape({
        code: PropTypes.string,
        is_applied: PropTypes.bool,
      }),
    }).isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id_product: PropTypes.number,
        quantity: PropTypes.number,
      }),
    ).isRequired,
  }).isRequired,
}

export default connect((state) => ({
  cart: state.cart.payload,
  customerId:
    state.auth?.user?.id_customer ?? state.auth?.customerData?.id_customer,
  isGuestMode: state.auth?.isGuestMode,
  shop: state.internationalization?.shop,
  country: state.internationalization?.country,
  language: state.internationalization?.language,
}))(VoucherCheckout)
